import {Config, usePlantConfig} from '@domain/shifthandover'
import {PlaceholderWrapper} from '@hconnect/common/components/placeholderWrapper'
import {mergeSx} from '@hconnect/uikit/src/lib2'
import {CircularProgress} from '@mui/material'
import React, {FC, useContext} from 'react'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router'

import {SimpleErrorPage} from '../pages/SimpleErrorPage'

const configContext = React.createContext<Config | undefined>(undefined)

type PathParameter = {
  plantId: string
}
type ProviderProps = {
  initialState: Config
  children: React.ReactNode
}

export const ConfigProvider = ({initialState, children}: ProviderProps) => (
  <configContext.Provider value={initialState}>{children}</configContext.Provider>
)

export const ConfigProviderWrapper: FC<{children: React.ReactNode}> = ({children}) => {
  const {plantId} = useParams<PathParameter>()
  if (!plantId) throw new Error('Missing plantId prop')
  const {i18n} = useTranslation()
  const {
    data: config,
    isError,
    isLoading,
    error
  } = usePlantConfig({plantId, language: i18n.language})

  if (isLoading) {
    return (
      <PlaceholderWrapper noBackground>
        <CircularProgress sx={mergeSx({color: 'common.white'})} />
      </PlaceholderWrapper>
    )
  }

  if (isError) {
    return (
      <SimpleErrorPage>
        <pre>{(error as Error)?.toString?.()}</pre>
      </SimpleErrorPage>
    )
  }

  return config ? <ConfigProvider initialState={config}>{children}</ConfigProvider> : null
}

export function useConfigData() {
  const ctx = useContext(configContext)
  if (ctx === undefined) {
    throw new Error('useConfig used outside of ConfigProvider')
  } else {
    return ctx
  }
}
